import * as Data9 from "common/src/data/Data9.bs";
import * as Data95 from "common/src/data/Data95.bs";
import * as Data10 from "common/src/data/Data10.bs";
import * as Data11 from "common/src/data/Data11.bs";
import * as Data12 from "common/src/data/Data12.bs";
import * as Data13 from "common/src/data/Data13.bs";
import * as Data14 from "common/src/data/Data14.bs";
import * as Data56 from "common/src/data/Data56.bs";
import * as Data76 from "common/src/data/Data76.bs";
import * as Data46 from "common/src/data/Data46.bs";
import * as Data101 from "common/src/data/Data101.bs";
import * as Data15 from "common/src/data/Data15.bs";
import * as Data151 from "common/src/data/Data151.bs";
import * as Data16 from "common/src/data/Data16.bs";
import * as Data17 from "common/src/data/Data17.bs";
import * as Data18 from "common/src/data/Data18.bs";
import * as Sd from "common/src/tft-static/Sd";
import {
  common,
  units,
  traits,
  items,
  augments,
} from "common/src/bindings/Translation2";

const getData = (set: number) => {
  switch (set) {
    case 110:
      return Data11;
    case 10:
    case 100:
      return Data10;
    case 95:
      return Data95;
    case 90:
      return Data9;
    case 120:
      return Data12;
    case 130:
      return Data13;
    case 140:
      return Data14;
    case 150:
      return Data15;
    case 151:
      return Data151;
    case 160:
      return Data16;
    case 170:
      return Data17;
    case 180:
      return Data18;
    case 56:
      return Data56;
    case 76:
      return Data76;
    case 46:
      return Data46;
    case 101:
      return Data101;
    default:
      throw new Error(`Unknown set for search ${set}`);
  }
};

function prepLabel(v: string): string {
  return v.replace(/[-'\. \/]/gm, "").toLowerCase();
}

// Op Map
// 0 -> EVAL
// 1 -> OR
// 2 -> AND
// 3 -> NOT

const extractBrackets = (query: string): [string, Array<string>] => {
  let currQuery = query;
  const expression = [];
  const currBracket = [];
  const bracketExps = [];

  let bracketIdx = 0;

  // Cap to 5 for no infinite loops
  for (let i = 0; i < 5; i++) {
    let bracketFound = false;
    let bracketStarted = false;
    for (let c of currQuery) {
      if (c === "(") {
        if (bracketStarted) {
          expression.push("(");
          for (let bc of currBracket) {
            expression.push(bc);
          }
          currBracket.length = 0;
        } else {
          bracketStarted = true;
          bracketFound = true;
        }
      } else if (c === ")") {
        if (bracketStarted) {
          if (currBracket.length > 0) {
            bracketExps.push(currBracket.join(""));
            currBracket.length = 0;
            expression.push("$");
            expression.push(String(bracketIdx));
            bracketIdx += 1;
          }
          bracketStarted = false;
        }
      } else {
        if (bracketStarted) {
          currBracket.push(c);
        } else {
          expression.push(c);
        }
      }
    }

    if (bracketStarted) {
      if (currBracket.length > 0) {
        bracketExps.push(currBracket.join(""));
        currBracket.length = 0;
        expression.push("$");
        expression.push(String(bracketIdx));
        bracketIdx += 1;
      }
    }

    if (!bracketFound) {
      break;
    } else {
      currQuery = expression.join("");
      expression.length = 0;
      currBracket.length = 0;
    }
  }

  return [expression.join(""), bracketExps];
};

type Expr = [0, string] | [1, Array<Expr>] | [2, Array<Expr>] | [3, Expr];

function extractVars(v: string, vars: Array<string>): Expr {
  if (v[0] === "$") {
    const idx = Number(v.slice(1));
    if (!isNaN(idx) && vars[idx]) {
      return parseOr(vars[idx], vars);
    }
  }

  return [0, v];
}

function extractNot(v: string, vars: Array<string>): Expr {
  if (v[0] === "!") {
    return [3, extractVars(v.slice(1), vars)];
  } else {
    return extractVars(v, vars);
  }
}

function extractAnd(v: string, vars: Array<string>): Expr {
  const andParts = v.split("&").filter((p) => p !== "");
  if (andParts.length == 1) {
    return extractNot(andParts[0], vars);
  } else {
    return [2, andParts.map((p) => extractNot(p, vars))];
  }
}

function parseOr(search: string, vars: Array<string>): Expr {
  const parts = search.split(/[,|，、]/gm).filter((p) => p !== "");
  if (parts.length === 1) {
    return extractAnd(parts[0], vars);
  } else {
    return [1, parts.map((p) => extractAnd(p, vars))];
  }
}

export const parseSearch = (query: string): Expr => {
  const search = query
    .toLowerCase()
    // Cleanup useless chars
    .replace(/[-'\s\.\/]/gm, "");

  if (search === "") {
    return [0, ""];
  }

  const [baseExp, bracketExps] = extractBrackets(search);
  const exp = parseOr(baseExp, bracketExps);
  return exp;
};

// if (typeof window !== "undefined") {
//   // @ts-ignore
//   window.parseSearch = parseSearch;
// }

// Op Map
// 0 -> EVAL
// 1 -> OR
// 2 -> AND
// 3 -> NOT
function matchSearch(exp: Expr, labels: Array<string>): boolean {
  if (exp[0] === 0) {
    let match = false;
    const search = exp[1];
    for (let label of labels) {
      match = match || Boolean(~label.indexOf(search));
    }
    return match;
  } else if (exp[0] === 1) {
    const parts = exp[1];
    let match = false;
    for (let v of parts) {
      match = match || matchSearch(v, labels);
    }
    return match;
  } else if (exp[0] === 2) {
    const parts = exp[1];
    let match = true;
    for (let v of parts) {
      match = match && matchSearch(v, labels);
    }
    return match;
  } else if (exp[0] === 3) {
    const v = exp[1];
    return !matchSearch(v, labels);
  }

  return false;
}

type SearchIndexEntry = {
  key: string;
  labels: Array<string>;
  fullLabels: Array<string>;
};

type SearchIndex = Array<SearchIndexEntry>;
type SearchIndexMap = { [id: string]: SearchIndexEntry };

function fancySearch2(
  baseQuery: string,
  index: Array<SearchIndexEntry>,
): Set<string> {
  const search = parseSearch(baseQuery);
  const output: Set<string> = new Set();

  for (let entry of index) {
    if (matchSearch(search, entry.fullLabels)) {
      output.add(entry.key);
    }
  }

  return output;
}

function fancySearch2Main(
  baseQuery: string,
  index: Array<SearchIndexEntry>,
): Set<string> {
  const search = parseSearch(baseQuery);
  const output: Set<string> = new Set();

  for (let entry of index) {
    if (matchSearch(search, entry.labels)) {
      output.add(entry.key);
    }
  }

  return output;
}

let unitsIndex: { [set: number]: SearchIndex } = {};
const getUnitsIndex = (set: number) => {
  if (unitsIndex[set] != null) {
    return unitsIndex[set];
  }

  const mainIndex: SearchIndex = [];

  const data = getData(set);

  for (let id of Sd.allUnitsAll(set)) {
    const label = units(id);
    const enLabel = data.units[id]?.name;

    const mainLabels = [];
    mainLabels.push(prepLabel(label));
    if (enLabel && label !== enLabel) {
      mainLabels.push(prepLabel(enLabel));
    }

    const altLabels = [];

    for (let t of Sd.getUnitTraits(id, set)) {
      const label = traits(t);
      const enLabel = data.traits[t]?.name;
      if (enLabel && label !== enLabel) {
        altLabels.push(prepLabel(enLabel));
      }
      altLabels.push(prepLabel(label));
    }
    mainLabels.push(Sd.unitTier(id) + "cost");

    mainIndex.push({
      key: id,
      labels: mainLabels,
      fullLabels: mainLabels.concat(altLabels),
    });
  }

  unitsIndex[set] = mainIndex;
  return mainIndex;
};
export const searchUnits = (query: string, set: number): Set<string> => {
  const index = getUnitsIndex(set);
  return fancySearch2(query, index);
};
export const searchUnitsArr = (query: string, set: number): Array<string> => {
  return Array.from(searchUnits(query, set));
};
export const searchUnitsExact = (query: string, set: number): Set<string> => {
  const index = getUnitsIndex(set);
  return fancySearch2Main(query, index);
};
export const searchUnitsExactArr = (
  query: string,
  set: number,
): Array<string> => {
  return Array.from(searchUnitsExact(query, set));
};
export const searchUnitsGroup = (
  search: Expr,
  unitIds: Array<string>,
  set: number,
): boolean => {
  const labels = [];

  for (let unitId of unitIds) {
    const unit = getIndexUnit(unitId, set);
    for (let label of unit.labels) {
      labels.push(label);
    }
  }

  return matchSearch(search, labels);
};

let unitsIndexMap: { [set: number]: SearchIndexMap } = {};
export const getIndexUnit = (unitId: string, set: number) => {
  if (unitsIndexMap[set] == null) {
    const index = getUnitsIndex(set);
    const map: SearchIndexMap = {};
    for (let entity of index) {
      map[entity.key] = entity;
    }
    unitsIndexMap[set] = map;
  }
  const indexMap = unitsIndexMap[set];

  return indexMap[unitId];
};

let traitsIndex: { [set: number]: SearchIndex } = {};
const getTraitsIndex = (set: number) => {
  if (traitsIndex[set] != null) {
    return traitsIndex[set];
  }

  const mainIndex: SearchIndex = [];
  const data = getData(set);

  for (let id of Sd.allTraits2(set)) {
    const label = traits(id);
    const enLabel = data.traits[id]?.name;

    const mainLabels = [];
    mainLabels.push(prepLabel(enLabel));
    if (label !== enLabel) {
      mainLabels.push(prepLabel(label));
    }
    mainIndex.push({ key: id, labels: mainLabels, fullLabels: mainLabels });
  }

  traitsIndex[set] = mainIndex;
  return mainIndex;
};
export const searchTraits = (query: string, set: number): Set<string> => {
  const index = getTraitsIndex(set);
  return fancySearch2(query, index);
};
export const searchTraitsArr = (query: string, set: number): Array<string> => {
  return Array.from(searchTraits(query, set));
};

let rankTraitsIndex: { [set: number]: SearchIndex } = {};
const getRankTraitsIndex = (set: number) => {
  if (rankTraitsIndex[set] != null) {
    return rankTraitsIndex[set];
  }

  const mainIndex: SearchIndex = [];
  const data = getData(set);

  for (let id of Sd.allTraits2(set)) {
    Sd.traitRankCounts(id, set)!.forEach((rc, idx) => {
      let rankId = `${id}-${idx + 1}`;
      const label = `${rc} ${traits(id)}`;
      const enLabel = `${rc} ${data.traits[id]?.name}`;

      const mainLabels = [];
      mainLabels.push(prepLabel(enLabel));
      if (label !== enLabel) {
        mainLabels.push(prepLabel(label));
      }
      mainIndex.push({
        key: rankId,
        labels: mainLabels,
        fullLabels: mainLabels,
      });
    });
  }

  rankTraitsIndex[set] = mainIndex;
  return mainIndex;
};
export const searchRankTraits = (query: string, set: number): Set<string> => {
  const index = getRankTraitsIndex(set);
  return fancySearch2(query, index);
};

type ItemTyp =
  | "component"
  | "craftable"
  | "emblem"
  | "ornn"
  | "radiant"
  | "support";

const getItemTyp = (id: number, set: number): ItemTyp | undefined => {
  if (id < 9) {
    return "component";
  } else {
    switch (set == 90 ? Data9.getItemTyp(id) : Data95.getItemTyp(id)) {
      case "Craftable":
        return "craftable";
      case "CraftableEmblem":
      case "ElusiveEmblem":
        return "emblem";
      case "Ornn":
        return "ornn";
      case "Support":
        return "support";
      case "Radiant":
        return "radiant";
    }
  }
};

let itemsIndex: { [set: number]: SearchIndex } = {};
const getItemsIndex = (set: number) => {
  if (itemsIndex[set] != null) {
    return itemsIndex[set];
  }

  const mainIndex: SearchIndex = [];
  const data = getData(set);

  for (let [id, info] of Object.entries(data.items)) {
    if (info.id < 9) {
      continue;
    }

    const label = items(id);
    const enLabel = info.name;

    const mainLabels = [];
    mainLabels.push(prepLabel(enLabel));
    if (label !== enLabel) {
      mainLabels.push(prepLabel(label));
    }

    const altLabels = [];
    const c1 = info?.from?.[0];
    if (c1 != null) {
      const [id, info] = data.itemsArr()[c1 - 1];
      const enC1Label = info.name;
      const c1Label = items(id);
      if (c1Label != enC1Label) {
        altLabels.push(prepLabel(c1Label));
      }
      altLabels.push(prepLabel(enC1Label));
    }
    const c2 = info?.from?.[1];
    if (c2 != null) {
      const [id, info] = data.itemsArr()[c2 - 1];
      const enC2Label = info.name;
      const c2Label = items(id);
      if (c2Label != enC2Label) {
        altLabels.push(prepLabel(c2Label));
      }
      altLabels.push(prepLabel(enC2Label));
    }

    if (info.trait) {
      altLabels.push(prepLabel(traits(info.trait)));
    }

    // @ts-ignore
    if (info.tags) {
      // @ts-ignore
      for (const tag of info.tags) {
        altLabels.push(tag.toLowerCase());
        if (tag === "Artifact") {
          altLabels.push("ornn");
        }
      }
    }
    // const typ = getItemTyp(info.id, set);
    // if (typ) {
    //   altLabels.push(typ);
    // }
    // if (typ === "ornn") {
    //   altLabels.push("artifact");
    // }

    mainIndex.push({
      key: id,
      labels: mainLabels,
      fullLabels: mainLabels.concat(altLabels),
    });
  }

  itemsIndex[set] = mainIndex;

  return mainIndex;
};

export const searchItems = (query: string, set: number): Set<string> => {
  const index = getItemsIndex(set);
  return fancySearch2(query, index);
};
export const searchItemsArr = (query: string, set: number): Array<string> => {
  return Array.from(searchItems(query, set));
};
let itemsIndexMap: { [set: number]: SearchIndexMap } = {};
export const getIndexItem = (itemId: string, set: number) => {
  if (itemsIndexMap[set] == null) {
    const index = getItemsIndex(set);
    const map: SearchIndexMap = {};
    for (let entity of index) {
      map[entity.key] = entity;
    }
    itemsIndexMap[set] = map;
  }
  const indexMap = itemsIndexMap[set];

  return indexMap[itemId];
};

export const searchUnitItems = (
  search: Expr,
  unitId: string | null,
  itemIds: Array<string>,
  set: number,
): boolean => {
  const labels = [];

  if (unitId != null) {
    const unit = getIndexUnit(unitId, set);
    for (let label of unit.labels) {
      labels.push(label);
    }
  }

  for (let itemId of itemIds) {
    let item = getIndexItem(itemId, set);
    if (item) {
      for (let label of item.fullLabels) {
        labels.push(label);
      }
    }
  }

  return matchSearch(search, labels);
};

const getAugmentTyp = (info: any): string | undefined => {
  if (info.hero != null) {
    return "hero";
  } else if (info.rank === 3) {
    return "prismatic";
  } else if (info.rank === 2) {
    return "gold";
  } else if (info.rank === 1) {
    return "silver";
  }
};

let augmentsIndex: { [set: number]: SearchIndex } = {};
const getAugmentsIndex = (set: number) => {
  if (augmentsIndex[set] != null) {
    return augmentsIndex[set];
  }

  const mainIndex: SearchIndex = [];
  const data = getData(set);

  const entries = Object.entries(data.augments);

  for (let [id, v] of entries) {
    const mainLabels = [];
    const enLabel = v.name;
    const label = augments(id);
    mainLabels.push(prepLabel(enLabel));
    if (label !== enLabel) {
      mainLabels.push(prepLabel(label));
    }

    const altLabels = [];
    if (v.tags && v.tags.includes("hero")) {
      altLabels.push("hero");
    }

    const typ = getAugmentTyp(v);
    if (typ) {
      altLabels.push(typ);
    }

    mainIndex.push({
      key: id,
      labels: mainLabels,
      fullLabels: mainLabels.concat(altLabels),
    });
  }

  augmentsIndex[set] = mainIndex;
  return mainIndex;
};

export const searchAugments = (query: string, set: number): Set<string> => {
  const index = getAugmentsIndex(set);
  return fancySearch2(query, index);
};
export const searchAugmentsArr = (
  query: string,
  set: number,
): Array<string> => {
  const out = Array.from(searchAugments(query, set));
  return out;
};

let anomaliesIndex: { [set: number]: SearchIndex } = {};
const getAnomaliesIndex = (set: number) => {
  if (anomaliesIndex[set] != null) {
    return anomaliesIndex[set];
  }

  const mainIndex: SearchIndex = [];
  const data = getData(set);

  // @ts-ignore
  for (let id of Object.keys(data.anomalies)) {
    const mainLabels = [];
    mainLabels.push(prepLabel(common(id)));
    mainLabels.push(prepLabel(common(id + "_typ")));
    mainLabels.push(prepLabel(common(id + "_desc")));
    mainIndex.push({
      key: id,
      labels: mainLabels,
      fullLabels: mainLabels,
    });
  }

  anomaliesIndex[set] = mainIndex;
  return mainIndex;
};

export const searchAnomalies = (query: string, set: number): Set<string> => {
  const index = getAnomaliesIndex(set);
  return fancySearch2(query, index);
};

export const searchAnomaliesArr = (
  query: string,
  set: number,
): Array<string> => {
  return Array.from(searchAnomalies(query, set));
};

let powerupsIndex: { [set: number]: SearchIndex } = {};
const getPowerupsIndex = (set: number) => {
  if (powerupsIndex[set] != null) {
    return powerupsIndex[set];
  }

  const mainIndex: SearchIndex = [];

  const data: any = getData(set);

  for (let id of Object.keys(data.powerups)) {
    const label = common(id);
    const enLabel = data.powerups[id]?.name;
    const mainLabels = [];
    mainLabels.push(prepLabel(label));
    if (enLabel && label !== enLabel) {
      mainLabels.push(prepLabel(enLabel));
    }

    mainIndex.push({
      key: id,
      labels: mainLabels,
      fullLabels: mainLabels,
    });
  }

  powerupsIndex[set] = mainIndex;
  return mainIndex;
};

export const searchPowerups = (query: string, set: number): Set<string> => {
  const index = getPowerupsIndex(set);
  return fancySearch2(query, index);
};

export const searchPowerupsArr = (
  query: string,
  set: number,
): Array<string> => {
  return Array.from(searchPowerups(query, set));
};
